import { useEffect, useRef, useState, type JSX } from 'react'
import { X, Check, ChevronDown, Loader2, Pencil, UserPlus, UserRound } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useClientGroups } from '../../../hooks/useClientGroups.js'
import { usePasswordPolicies } from '../../../hooks/usePasswordPolicies.js'
import type { ClientRecord } from '../../../hooks/useClients.js'
import type { ClientFormField } from '../../forms/DynamicClientFields.js'
import ToggleSwitch from '../ToggleSwitch.js'
import {
  FormContainer,
  FormSection,
  FormGrid,
  FormField,
  FormInput,
  FormSelect,
  PasswordField,
} from '../../../components/forms/layout/index.js'
import DatePicker from '../../ui/DatePicker.js'

export type CreateClientPanelMode = 'create' | 'edit' | 'view'

interface CreateClientPanelProps {
  open: boolean
  onClose: () => void
  onCreated: (client: ClientRecord) => void
  mode?: CreateClientPanelMode
  clientId?: string
  /** Wording only — "Customer" on Customer 360, "Client" elsewhere. */
  noun?: 'Client' | 'Customer'
}

// 3px border radius throughout — matches CreateConversationPanel.tsx.
const inputClass =
  'w-full rounded-[3px] border bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[#DCE7F8] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF] disabled:cursor-not-allowed disabled:opacity-60'

function SelectField({
  icon, value, onChange, disabled, placeholder, error, options,
}: {
  icon?: JSX.Element | undefined
  value: string
  onChange: (v: string) => void
  disabled?: boolean | undefined
  placeholder: string
  error?: string | undefined
  options: { value: string; label: string }[]
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const selected = options.find((o) => o.value === value)

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`${inputClass} flex items-center justify-between text-left ${icon ? 'pl-9' : ''} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${error ? 'border-red-300' : 'border-slate-200'}`}
      >
        {icon && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span>}
        <span className={`truncate ${selected ? 'text-slate-800' : 'text-slate-400'}`}>{selected ? selected.label : placeholder}</span>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1.5 max-h-56 w-full overflow-y-auto rounded-[3px] border border-slate-200 bg-white py-1 shadow-xl">
          {options.length === 0 && <div className="px-3 py-3 text-center text-xs text-slate-400">No options</div>}
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false) }}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
            >
              <span className="truncate">{o.label}</span>
              {value === o.value && <Check className="ml-2 h-4 w-4 shrink-0 text-[#3366FF]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Dynamic (per-group) field renderer — mirrors DynamicClientFields.tsx's field_type switch, restyled to inputClass/FormField. ── */
function DynamicFieldInput({ field, value, onChange, disabled, error }: {
  field: ClientFormField
  value: unknown
  onChange: (v: unknown) => void
  disabled: boolean
  error?: string | undefined
}): JSX.Element {
  const strValue = value === undefined || value === null ? '' : String(value)
  const borderClass = error ? 'border-red-300' : 'border-slate-200'

  switch (field.field_type) {
    case 'number':
      return <input type="number" disabled={disabled} value={strValue} onChange={(e) => onChange(e.target.value)} placeholder={`Enter ${field.title}`} className={`${inputClass} ${borderClass}`} />
    case 'email':
      return <input type="email" disabled={disabled} value={strValue} onChange={(e) => onChange(e.target.value)} placeholder={`Enter ${field.title}`} maxLength={field.max_length || 255} className={`${inputClass} ${borderClass}`} />
    case 'date':
      return <DatePicker ariaLabel={field.title} size="sm" value={strValue} onChange={onChange} disabled={disabled} />
    case 'dropdown':
      return (
        <SelectField
          value={strValue}
          onChange={onChange}
          disabled={disabled}
          placeholder={`Select ${field.title}`}
          error={error}
          options={(field.field_data ?? []).map((item) => ({ value: item, label: item }))}
        />
      )
    case 'checkbox':
      return (
        <SelectField
          value={strValue}
          onChange={onChange}
          disabled={disabled}
          placeholder={`Select ${field.title}`}
          error={error}
          options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
        />
      )
    case 'textarea':
      return <textarea rows={3} disabled={disabled} value={strValue} onChange={(e) => onChange(e.target.value)} placeholder={`Enter ${field.title}`} maxLength={field.max_length || 1000} className={`${inputClass} resize-none ${borderClass}`} />
    default:
      return <input type="text" disabled={disabled} value={strValue} onChange={(e) => onChange(e.target.value)} placeholder={`Enter ${field.title}`} maxLength={field.max_length || 255} className={`${inputClass} ${borderClass}`} />
  }
}

const EMAIL_RE = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

function validateEmail(email: string): string {
  if (!email.trim()) return 'Email is required'
  if (!EMAIL_RE.test(email)) return 'Please enter a valid email format (e.g., user@example.com)'
  return ''
}
function validateFullName(name: string): string {
  if (!name.trim()) return 'Full name is required'
  if (name.length > 128) return 'Full name must not exceed 128 characters'
  return ''
}
function sanitizePhone(raw: string): string {
  let digits = 0
  let result = ''
  for (const ch of raw) {
    if (ch === '+' && result.length === 0) {
      result = '+'
    } else if (/[0-9]/.test(ch) && digits < 15) {
      result += ch
      digits++
    }
  }
  return result
}

function validatePhone(phone: string): string {
  if (!phone.trim()) return 'Phone number is required'
  if (!/^[0-9+]+$/.test(phone)) return 'Phone number can only contain digits and an optional +'
  if (phone.includes('+') && !phone.startsWith('+')) return '+ must be at the start of the phone number'
  const digits = phone.replace(/[^0-9]/g, '').length
  if (digits < 10) return 'Phone number must contain at least 10 digits'
  if (digits > 15) return 'Phone number must not exceed 15 digits'
  return ''
}

export default function CreateClientPanel({ open, onClose, onCreated, mode = 'create', clientId, noun = 'Client' }: CreateClientPanelProps): JSX.Element | null {
  const isView = mode === 'view'
  const isEdit = mode === 'edit'
  const { groups, fetchFormFields } = useClientGroups(open)
  const { items: policies, fetchItems: fetchPolicies } = usePasswordPolicies()

  const [groupId, setGroupId] = useState('')
  const [dynamicFields, setDynamicFields] = useState<ClientFormField[]>([])
  const [dynamicValues, setDynamicValues] = useState<Record<string, unknown>>({})
  const [dynamicErrors, setDynamicErrors] = useState<Record<string, string>>({})

  const [status, setStatus] = useState(true)
  const [locked, setLocked] = useState(false)
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const activePolicy = policies.find((p) => p.status) ?? policies[0] ?? null

  const touch = (key: string) => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }))

  const reset = () => {
    setGroupId('')
    setDynamicFields([])
    setDynamicValues({})
    setDynamicErrors({})
    setStatus(true)
    setLocked(false)
    setFullName('')
    setPhone('')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setErrors({})
    setTouched({})
    setSubmitError(null)
  }

  // Load password policy + (edit/view) the existing client once the panel opens.
  useEffect(() => {
    if (!open) return
    void fetchPolicies()
    if ((isEdit || isView) && clientId) {
      setLoading(true)
      api.get<{ client: ClientRecord }>(`/admin/clients/${clientId}`)
        .then(async (res) => {
          const c = res.client
          setGroupId(c.group_id)
          setStatus(c.status)
          setLocked(c.locked)
          setFullName(c.full_name ?? '')
          setPhone(c.phone_number ?? '')
          setEmail(c.email)
          setDynamicValues(c.additionalFields ?? {})
          if (c.group_id) {
            setDynamicFields(await fetchFormFields(c.group_id))
          }
        })
        .catch((e: unknown) => setSubmitError(e instanceof Error ? e.message : 'Failed to load client'))
        .finally(() => setLoading(false))
    }
  }, [open, clientId, isEdit, isView, fetchPolicies, fetchFormFields])

  useEffect(() => {
    if (!open) reset()
  }, [open])

  const validatePassword = (): string => {
    if (isEdit && !password) return ''
    if (!password) return 'Password is required'
    const min = activePolicy?.minimumPasswordLength ?? 8
    if (password.length < min) return `Password must be at least ${min} characters`
    if ((activePolicy?.requireUppercase ?? true) && !/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter'
    if ((activePolicy?.requireLowercase ?? true) && !/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter'
    if ((activePolicy?.requireNumber ?? true) && !/[0-9]/.test(password)) return 'Password must contain at least one digit'
    if ((activePolicy?.requireNonAlphanumeric ?? true) && !/[^a-zA-Z0-9]/.test(password)) return 'Password must contain at least one special character'
    return ''
  }

  // Real-time validation: recompute errors for touched fields whenever form data changes.
  useEffect(() => {
    if (!open) return

    const staticErrs: Record<string, string> = {}
    if (!groupId) staticErrs.group = 'Client group is required'
    const fn = validateFullName(fullName)
    if (fn) staticErrs.full_name = fn
    const pn = validatePhone(phone)
    if (pn) staticErrs.phone_number = pn
    const em = validateEmail(email)
    if (em) staticErrs.email = em
    const pw = validatePassword()
    if (pw) staticErrs.password = pw
    if (password && password !== confirmPassword) staticErrs.confirm_password = 'Passwords do not match'
    else if (!isEdit && !confirmPassword) staticErrs.confirm_password = 'Please confirm the password'

    const dynErrs: Record<string, string> = {}
    for (const f of dynamicFields) {
      if (f.is_required && !dynamicValues[f.name]) dynErrs[f.name] = `${f.title} is required`
    }

    setErrors(() => {
      const next: Record<string, string> = {}
      for (const key of Object.keys(touched)) {
        if (staticErrs[key]) next[key] = staticErrs[key]
      }
      return next
    })
    setDynamicErrors(() => {
      const next: Record<string, string> = {}
      for (const key of Object.keys(touched)) {
        if (dynErrs[key]) next[key] = dynErrs[key]
      }
      return next
    })
  }, [open, groupId, fullName, phone, email, password, confirmPassword, isEdit, activePolicy, dynamicFields, dynamicValues, touched])

  const validate = (): boolean => {
    const staticErrs: Record<string, string> = {}
    if (!groupId) staticErrs.group = 'Client group is required'
    const fn = validateFullName(fullName)
    if (fn) staticErrs.full_name = fn
    const pn = validatePhone(phone)
    if (pn) staticErrs.phone_number = pn
    const em = validateEmail(email)
    if (em) staticErrs.email = em
    const pw = validatePassword()
    if (pw) staticErrs.password = pw
    if (password && password !== confirmPassword) staticErrs.confirm_password = 'Passwords do not match'
    else if (!isEdit && !confirmPassword) staticErrs.confirm_password = 'Please confirm the password'

    const dynErrs: Record<string, string> = {}
    for (const f of dynamicFields) {
      if (f.is_required && !dynamicValues[f.name]) dynErrs[f.name] = `${f.title} is required`
    }

    setErrors(staticErrs)
    setDynamicErrors(dynErrs)
    setTouched((prev) => {
      const next = { ...prev }
      for (const k of ['group', 'full_name', 'phone_number', 'email', 'password', 'confirm_password']) {
        next[k] = true
      }
      for (const f of dynamicFields) {
        next[f.name] = true
      }
      return next
    })

    return Object.keys(staticErrs).length === 0 && Object.keys(dynErrs).length === 0
  }

  if (!open) return null

  const handleGroupChange = async (id: string) => {
    setGroupId(id)
    setDynamicValues({})
    setDynamicErrors({})
    if (id) {
      setDynamicFields(await fetchFormFields(id))
    } else {
      setDynamicFields([])
    }
  }

  const handleSubmit = async () => {
    if (submitting || isView || !validate()) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const payload: Record<string, unknown> = {
        status, locked, group_id: groupId, email: email.trim(),
        full_name: fullName.trim(), phone_number: phone.trim(),
        additional_fields: dynamicValues,
      }
      if (!isEdit || password) {
        payload.password = password
        payload.confirm_password = confirmPassword
      }
      const res = isEdit
        ? await api.put<{ client: ClientRecord }>(`/admin/clients/${clientId}`, payload)
        : await api.post<{ client: ClientRecord }>('/admin/clients', payload)
      onCreated(res.client)
      onClose()
    } catch (e) {
      const body = e instanceof Error ? (e as Error & { body?: { issues?: { message: string }[] } }).body : undefined
      setSubmitError(body?.issues?.[0]?.message ?? (e instanceof Error ? e.message : 'Failed to save client'))
    } finally {
      setSubmitting(false)
    }
  }

  const title = isView ? `View ${noun}` : isEdit ? `Edit ${noun}` : `New ${noun}`
  const subtitle = isView
    ? `${noun} details`
    : isEdit
      ? `Update ${fullName || email || `this ${noun.toLowerCase()}`}`
      : `Add a ${noun.toLowerCase()} — fields marked * are required`
  const groupOptions = groups.map((g) => ({ label: g.name, value: g.id }))
  const passwordHelp = `Min ${activePolicy?.minimumPasswordLength ?? 8} chars${
    activePolicy?.requireUppercase ? ', uppercase' : ''
  }${activePolicy?.requireLowercase ? ', lowercase' : ''}${
    activePolicy?.requireNumber ? ', digit' : ''
  }${activePolicy?.requireNonAlphanumeric ? ', special char' : ''}`

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-[2px] transition-opacity" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-3xl flex-col border-l border-slate-200 bg-slate-50 shadow-2xl transition-transform">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] border border-blue-200 bg-blue-50 text-[#3366FF]">
              {isEdit ? <Pencil className="h-4 w-4" /> : isView ? <UserRound className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold leading-tight text-slate-900">{title}</h2>
              <p className="truncate text-xs text-slate-500">{subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-[3px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" title="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-slate-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading {noun.toLowerCase()}…
            </div>
          ) : (
            <FormContainer className="!space-y-4">
              <FormSection title="Contact details" description={`How to reach this ${noun.toLowerCase()}.`}>
                <FormGrid columns={2}>
                  <FormInput
                    id="client-full_name"
                    label="Full Name"
                    required
                    value={fullName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setFullName(e.target.value); touch('full_name') }}
                    onBlur={() => touch('full_name')}
                    placeholder="e.g. Priya Sharma"
                    maxLength={128}
                    disabled={isView}
                    error={errors.full_name}
                  />
                  <FormInput
                    id="client-phone_number"
                    label="Phone Number"
                    type="tel"
                    keyfilter="num"
                    inputMode="numeric"
                    required
                    value={phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPhone(sanitizePhone(e.target.value)); touch('phone_number') }}
                    onBlur={() => touch('phone_number')}
                    placeholder="+919876543210"
                    maxLength={20}
                    disabled={isView}
                    error={errors.phone_number}
                  />
                  <div className="md:col-span-2">
                    <FormInput
                      id="client-email"
                      label="Email Address"
                      type="email"
                      required
                      value={email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEmail(e.target.value); touch('email') }}
                      onBlur={() => touch('email')}
                      placeholder="name@example.com"
                      disabled={isView}
                      error={errors.email}
                    />
                  </div>
                </FormGrid>
              </FormSection>

              <FormSection title="Account" description="Group, status and portal access.">
                <FormGrid columns={3}>
                  <FormSelect
                    id="client-group"
                    label="Client Group"
                    required
                    value={groupId}
                    onChange={(e) => { void handleGroupChange(e.value as string); touch('group') }}
                    onBlur={() => touch('group')}
                    options={groupOptions}
                    filter
                    placeholder="Select a group"
                    disabled={isView}
                    error={errors.group}
                  />
                  <FormField label="Status" error={errors.status}>
                    <ToggleSwitch checked={status} onChange={(v) => { setStatus(v); touch('status') }} disabled={isView} onLabel="Active" offLabel="Inactive" size="sm" />
                  </FormField>
                  <FormField label="Portal access" error={errors.locked}>
                    <ToggleSwitch checked={locked} onChange={(v) => { setLocked(v); touch('locked') }} disabled={isView} onLabel="Locked" offLabel="Unlocked" size="sm" />
                  </FormField>
                </FormGrid>
              </FormSection>

              {!isView && (
                <FormSection
                  title="Portal password"
                  description={isEdit ? 'Leave both blank to keep the current password.' : `Login for the ${noun.toLowerCase()} portal.`}
                >
                  <FormGrid columns={2}>
                    <PasswordField
                      id="client-password"
                      label="Password"
                      required={!isEdit}
                      value={password}
                      onChange={(v) => { setPassword(v); touch('password') }}
                      onBlur={() => touch('password')}
                      placeholder={isEdit ? 'Unchanged' : 'Enter password'}
                      disabled={isView}
                      error={errors.password}
                      help={passwordHelp}
                    />
                    <PasswordField
                      id="client-confirm_password"
                      label="Confirm Password"
                      required={!isEdit}
                      value={confirmPassword}
                      onChange={(v) => { setConfirmPassword(v); touch('confirm_password') }}
                      onBlur={() => touch('confirm_password')}
                      placeholder={isEdit ? 'Unchanged' : 'Re-enter password'}
                      disabled={isView}
                      error={errors.confirm_password}
                    />
                  </FormGrid>
                </FormSection>
              )}

              {dynamicFields.length > 0 && (
                <FormSection title="Additional information" description="Fields defined for the selected group.">
                  <FormGrid columns={2}>
                    {dynamicFields.map((f) => (
                      <FormField key={f.name} label={f.title} required={f.is_required} error={dynamicErrors[f.name]}>
                        <DynamicFieldInput
                          field={f}
                          value={dynamicValues[f.name]}
                          onChange={(v) => { setDynamicValues((prev) => ({ ...prev, [f.name]: v })); touch(f.name) }}
                          disabled={isView || f.is_static}
                          error={dynamicErrors[f.name]}
                        />
                      </FormField>
                    ))}
                  </FormGrid>
                </FormSection>
              )}
            </FormContainer>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 border-t border-slate-200 bg-white px-6 py-3.5">
          {submitError && <span className="mr-auto truncate text-xs font-medium text-red-600">{submitError}</span>}
          <button onClick={onClose} disabled={submitting} className="rounded-[3px] border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50">
            {isView ? 'Close' : 'Cancel'}
          </button>
          {!isView && (
            <button onClick={() => void handleSubmit()} disabled={submitting} className="flex items-center gap-1.5 rounded-[3px] bg-[#3366FF] px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#2952CC] disabled:opacity-60">
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : `Create ${noun.toLowerCase()}`}
            </button>
          )}
        </div>
      </div>
    </>
  )
}
